import { NextPage } from 'next'
import { useLazyQuery } from '@apollo/client'
import styles from '../_artistPage.module.scss'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThLarge, faThList } from '@fortawesome/free-solid-svg-icons'
import classnames from 'classnames'
import ListSort from '../../../../components/ui/ListSort'
import { staticPath, staticProps } from '../../../../utils/createApolloClient'
import dynamic from 'next/dynamic'
import { SONGS_LIST_QUERY } from '../../../../gql/queries/songs_list_query'
import { ARTISTS_LIST_QUERY } from '../../../../gql/queries/artists_list_query'
import { ARTIST_DETAILS_QUERY } from '../../../../gql/queries/artist_details_query'

import ArtistLayout from '../components/ArtistLayout'
import { Artist, Song, SortSongsEnum } from '../../../../__generated__/graphql'
import useInView from 'react-cool-inview'

const SongsList = dynamic(() => import('../../../songs/Components/SongsList'))

// Cap initial mount for large catalogs; one height reserve for the rest (no DOM fan-out).
const INITIAL_VISIBLE_SONGS = 12

/** Measure one catalog row from already-rendered song cards (responsive). */
const measureCatalogRow = (holder: HTMLElement, isGrid: boolean) => {
  const articles = Array.from(
    holder.querySelectorAll(':scope > article')
  ) as HTMLElement[]
  if (articles.length === 0) return { cols: 1, rowHeight: 0 }

  if (isGrid) {
    const firstTop = articles[0].offsetTop
    const firstRow = articles.filter(
      (article) => Math.abs(article.offsetTop - firstTop) < 2
    )
    const cols = Math.max(firstRow.length, 1)
    const nextRow = articles.find((article) => article.offsetTop > firstTop + 2)
    const rowHeight = nextRow
      ? nextRow.offsetTop - firstTop
      : Math.max(...firstRow.map((article) => article.offsetHeight))
    return { cols, rowHeight }
  }

  const first = articles[0]
  const second = articles[1]
  const rowHeight = second
    ? second.offsetTop - first.offsetTop
    : first.offsetHeight +
      (parseFloat(getComputedStyle(first).marginBottom) || 0)
  return { cols: 1, rowHeight }
}

export const SearchContext = createContext(null)

export interface FiltersState {
  orderBy: SortSongsEnum
  perPage: number
}

export const initalFiltersState: FiltersState = {
  orderBy: SortSongsEnum.CurrentlyTrending,
  perPage: 1000
}

const ArtistSongsPage: NextPage<{ artist: Artist; ssrSongs: Song[] }> = ({
  artist,
  ssrSongs
}) => {
  if (!artist) return <></>

  const [filtersState, setFiltersState] =
    useState<FiltersState>(initalFiltersState)
  const [view, setView] = useState('list')

  const [performSearch, { data: performSearchData }] =
    useLazyQuery(SONGS_LIST_QUERY)

  const initialRender = useRef(true)
  useEffect(() => {
    if (!initialRender.current)
      performSearch({
        variables: { artistIds: [artist.id], ...filtersState }
      })
    else initialRender.current = false
  }, [filtersState])

  const songs = (performSearchData?.songs || ssrSongs || []) as Song[]

  const { observe, inView } = useInView({
    onEnter: ({ unobserve }) => unobserve()
  })

  const remainingCount = Math.max(0, songs.length - INITIAL_VISIBLE_SONGS)
  const isGridView = view === 'table'

  const listHolderRef = useRef<HTMLDivElement>(null)
  const [rowMetrics, setRowMetrics] = useState({ cols: 1, rowHeight: 0 })

  const updateRowMetrics = useCallback(() => {
    const holder = listHolderRef.current
    if (!holder) return
    const next = measureCatalogRow(holder, isGridView)
    if (next.rowHeight <= 0) return
    setRowMetrics((prev) =>
      prev.cols === next.cols && prev.rowHeight === next.rowHeight ? prev : next
    )
  }, [isGridView])

  useEffect(() => {
    updateRowMetrics()
    const holder = listHolderRef.current
    if (!holder || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateRowMetrics)
      return () => window.removeEventListener('resize', updateRowMetrics)
    }

    const observer = new ResizeObserver(() => updateRowMetrics())
    observer.observe(holder)
    window.addEventListener('resize', updateRowMetrics)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateRowMetrics)
    }
  }, [updateRowMetrics, songs.length, view])

  const deferredRows = isGridView
    ? Math.ceil(remainingCount / Math.max(rowMetrics.cols, 1))
    : remainingCount
  const deferredHeight =
    rowMetrics.rowHeight > 0 ? deferredRows * rowMetrics.rowHeight : 0

  return (
    <ArtistLayout artist={artist}>
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState
        }}
      >
        <header className={styles.tabsHeader}>
          <div className={classnames(styles.viewSelect)}>
            <div className="columns">
              <div className="column">
                <ListSort SearchContext={SearchContext} />
              </div>

              <div className={classnames(styles.noPaddingsColumn, 'column')}>
                <button
                  className={classnames(styles.tabLink, {
                    [styles.tabLinkActive]: view == 'list'
                  })}
                  onClick={() => setView('list')}
                >
                  <FontAwesomeIcon icon={faThList} size="2x" />
                </button>
              </div>

              <div className={classnames(styles.noPaddingsColumn, 'column')}>
                <button
                  className={classnames(styles.tabLink, {
                    [styles.tabLinkActive]: view == 'table'
                  })}
                  onClick={() => setView('table')}
                >
                  <FontAwesomeIcon icon={faThLarge} size="2x" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className={styles.panel}>
          <div
            ref={listHolderRef}
            className={classnames(styles.songsListHolder, {
              [styles.tableView]: isGridView
            })}
          >
            <SongsList
              songs={songs.slice(0, INITIAL_VISIBLE_SONGS)}
              isFlat={view == 'list'}
              inList={view == 'list'}
            />
            {remainingCount > 0 && !inView && (
              <div
                ref={observe}
                className={styles.catalogDeferred}
                style={
                  deferredHeight > 0 ? { height: deferredHeight } : undefined
                }
                aria-hidden
              />
            )}
            {remainingCount > 0 && inView && (
              <SongsList
                songs={songs.slice(INITIAL_VISIBLE_SONGS)}
                isFlat={view == 'list'}
                inList={view == 'list'}
              />
            )}
          </div>
        </div>
      </SearchContext.Provider>
    </ArtistLayout>
  )
}

export const getStaticPaths = staticPath({
  additionalPaths: async ({ client }) => {
    const { data } = await client.query({
      query: ARTISTS_LIST_QUERY
    })

    const paths = data.artists.map((artist) => ({
      params: { id: artist.id.toString() }
    }))

    return {
      paths,
      fallback: true
    }
  }
})

export const getStaticProps = staticProps({
  additionalProps: async ({ client, ctx }) => {
    const {
      data: { artist }
    } = await client.query({
      query: ARTIST_DETAILS_QUERY,
      variables: { id: Number(ctx.params.id) }
    })
    const {
      data: { songs: ssrSongs }
    } = await client.query({
      query: SONGS_LIST_QUERY,
      variables: {
        artistIds: [Number(ctx.params.id)],
        ...initalFiltersState
      }
    })
    return {
      artist,
      ssrSongs
    }
  }
})

export default ArtistSongsPage
